import {createContext, ReactNode, useCallback, useEffect, useState} from 'react'

import {ChatMessages} from '../../types/graphqlSchema'

const ChatContext = createContext<ChatContextProps>({} as ChatContextProps)

const ChatContextProvider = ({
  children, isBulkActionsEnabled = true, isToolbarVisible = true, ...props
}: ChatContextProviderProps) => {
  const [bulkAction, setBulkAction] = useState<BulkAction>()
  const [
    selectedChatMessages,
    setSelectedChatMessages,
  ] = useState(new Map<string, ChatMessages>())
  const [
    isForwardChatMessagesDialogOpen,
    setIsForwardChatMessagesDialogOpen,
  ] = useState(false)
  const onSetBulkAction = useCallback(
    (action: BulkAction) => setBulkAction(prev => {
      if (prev) {
        setSelectedChatMessages(new Map())
      }
      return action
    }),
    [],
  )
  const onUnsetBulkAction = useCallback(() => {
    setSelectedChatMessages(new Map())
    setBulkAction(undefined)
  }, [])
  const onToggleIsChatMessageSelected = useCallback((c: ChatMessages) => {
    setSelectedChatMessages(prev => {
      const map = new Map(prev)
      map.has(c.id) ? map.delete(c.id) : map.set(c.id, c)
      return map
    })
  }, [])
  const onToggleIsForwardingChatMessagesDialogOpen = useCallback(
    () => setIsForwardChatMessagesDialogOpen(s => !s), [],
  )
  const visibleToolbar = isToolbarVisible ? (
    bulkAction ? 'CHAT_BULK_ACTIONS_TOOLBAR' : 'CHAT_TOOLBAR'
  ) : undefined
  const [repliedChatMessage, setRepliedChatMessage] = useState<ChatMessages>()
  useEffect(() => {
    setBulkAction(undefined)
    setSelectedChatMessages(new Map())
  }, [props.chatId])
  return (
    <ChatContext.Provider
      value={{
        ...props,
        bulkAction,
        currentlyRepliedChatMessage: repliedChatMessage,
        isBulkActionsEnabled,
        isForwardChatMessagesDialogOpen,
        onSetBulkAction,
        onToggleIsChatMessageSelected,
        onToggleIsForwardingChatMessagesDialogOpen,
        onUnsetBulkAction,
        selectedChatMessages,
        setCurrentlyRepliedChatMessage: setRepliedChatMessage,
        visibleToolbar,
      }}
    >
      {children}
    </ChatContext.Provider>
  )
}

interface ChatContextProps extends Omit<
  ChatContextProviderProps,
  'children' | 'isToolbarVisible'
> {
  bulkAction: BulkAction | undefined
  currentlyRepliedChatMessage: ChatMessages | undefined
  isForwardChatMessagesDialogOpen: boolean
  onSetBulkAction: (_: BulkAction) => void
  onToggleIsChatMessageSelected: (_: ChatMessages) => void
  onToggleIsForwardingChatMessagesDialogOpen: () => void
  selectedChatMessages: SelectedChatMessages
  setCurrentlyRepliedChatMessage: (chatMessage: ChatMessages | undefined) => void
  onUnsetBulkAction: () => void
  visibleToolbar?: 'CHAT_TOOLBAR' | 'CHAT_BULK_ACTIONS_TOOLBAR'
}

type SelectedChatMessages = Map<string, ChatMessages>

interface ChatContextProviderProps {
  children?: ReactNode | Iterable<ReactNode>
  channelId?: string // chats of type INTERNAL have no channel
  chatId: string
  chatMessageId?: string
  isBulkActionsEnabled?: boolean
  isToolbarVisible?: boolean
}

type BulkAction = 'FORWARD' | 'DOWNLOAD'

export {ChatContext, ChatContextProvider}
